import React, { useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../../config/supabase';
import { updateDraftPurchaseOrder, sendDraftPurchaseOrder, deleteDraftPurchaseOrder, updateDraftItems, addDraftItem, formatPoCurrency, computeDraftSendEligibility } from '../../services/purchaseOrderService';
import { buildVendorContactInfo } from '../../utils/vendorContacts';
import { resolvePoLinePackSize, formatPoOrderUnit, buildPoLineDisplay, resolvePoLineBilling } from '../../utils/poLinePack';
import { buildDeliveryDatePresets, describeDeliveryDate, formatLocalDateTime } from '../../utils/deliveryDatePresets';
import useHideMobileNav from '../../utils/useHideMobileNav';
import VendorContactModal from './VendorContactModal';
import OrderingRulesEditor from './OrderingRulesEditor';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DANGER, BTN_TERTIARY, ALERT_ERROR, ALERT_SUCCESS, FOCUS_RING_CSS } from './orderingStyles';

// Mirrors the SQL `public.normalize_vendor_name(input_text)` function so
// name-based vendor lookups hit `vendors.normalized_display_name`, which
// the database maintains via a trigger using the same algorithm.
const normalizeVendorNameForLookup = (value) => String(value ?? '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const VENDOR_CONTACT_SELECT = (
  'id, display_name, status,'
  + ' contact_name, contact_email, contact_phone,'
  + ' contact2_name, contact2_email, contact2_phone,'
  + ' general_email, general_phone,'
  + ' send_orders_to_contact_email, send_orders_to_general_email'
);

const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(43,29,20,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  sheet: { background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 680, maxHeight: 'calc(92vh - env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 -8px 40px rgba(43,29,20,0.22)' },
  header: { padding: '18px 20px 14px', borderBottom: '1px solid #e8e0d5', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 },
  title: { fontSize: 18, fontWeight: 800, color: '#2b1d14', margin: 0, lineHeight: 1.3 },
  subtitle: { fontSize: 13, color: '#7b6b61', marginTop: 2 },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#5A4A42', padding: '4px 8px', lineHeight: 1, minHeight: 44, minWidth: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  body: { flex: 1, overflowY: 'auto', padding: '16px 20px', WebkitOverflowScrolling: 'touch' },
  section: { marginBottom: 20 },
  sectionLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#7b6b61', marginBottom: 8 },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#5A4A42', marginBottom: 5 },
  helper: { fontSize: 11, color: '#7b6b61', marginTop: 3 },
  input: { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d5c9c0', fontSize: 14, color: '#2b1d14', background: '#fff', outline: 'none', fontFamily: 'inherit' },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d5c9c0', fontSize: 14, color: '#2b1d14', background: '#fff', outline: 'none', fontFamily: 'inherit', resize: 'vertical', minHeight: 70 },
  qtyInput: { width: 64, padding: '6px 7px', borderRadius: 6, border: '1.5px solid #d5c9c0', fontSize: 13, color: '#2b1d14', background: '#fff', outline: 'none', fontFamily: 'inherit', textAlign: 'right', minHeight: 36 },
  removeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#b71c1c', fontWeight: 700, fontSize: 16, padding: '4px 6px', lineHeight: 1, minHeight: 36, minWidth: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#f7f1ea', borderBottom: '1.5px solid #e8e0d5', padding: '6px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#7b6b61', textTransform: 'uppercase' },
  td: { borderBottom: '1px solid #f0ebe4', padding: '7px 8px', color: '#2b1d14', verticalAlign: 'middle' },
  tdNum: { borderBottom: '1px solid #f0ebe4', padding: '7px 8px', color: '#2b1d14', textAlign: 'right', fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle' },
  footer: { padding: '14px 20px', paddingBottom: 'max(14px, calc(14px + env(safe-area-inset-bottom, 0px)))', borderTop: '1px solid #e8e0d5', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', flexShrink: 0, background: '#fff' },
  btnPrimary: { ...BTN_PRIMARY, fontSize: 14 },
  btnSecondary: { ...BTN_SECONDARY, fontSize: 14 },
  btnTertiary: { ...BTN_TERTIARY, fontSize: 13 },
  btnDanger: { ...BTN_DANGER, fontSize: 14 },
  alert: { borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 },
  alertAmended: { background: '#fff3cd', border: '1.5px solid #ffc107', color: '#7b4f00' },
  alertError: ALERT_ERROR,
  alertSuccess: ALERT_SUCCESS,
  emailRow: { background: '#f7f1ea', border: '1.5px solid #e8e0d5', borderRadius: 8, padding: '8px 12px', marginTop: 6, fontSize: 13, color: '#2b1d14' },
  noEmail: { background: '#fde8e8', border: '1.5px solid #ef9a9a', borderRadius: 8, padding: '8px 12px', marginTop: 6, fontSize: 13, color: '#8b0000' },
  // ── Pills ──────────────────────────────────────────────────────────────
  // The same chip the shopping review uses, so the two screens read as one
  // system: every fact about the order — line count, total, delivery day,
  // who it goes to — is a pill, and the pills line up down the left edge
  // where a thumb can run over them.
  pillRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  pill: (colors) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999,
    background: colors.bg, color: colors.fg, fontSize: 11.5, fontWeight: 700,
    letterSpacing: '0.02em', whiteSpace: 'nowrap', border: `1px solid ${colors.fg}22`,
  }),
  // Delivery-day presets, in two rows: the two near-term days get half the
  // width each because "Tomorrow" is a long word, and the five weekday
  // abbreviations share the row below at equal width. Grid, not flex-wrap —
  // wrapping left a ragged last row of two very wide buttons.
  dayRowNear: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 },
  dayRowWeek: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginTop: 6 },
  dayBtn: (active) => ({
    width: '100%', minWidth: 0, minHeight: 44, padding: '8px 4px', borderRadius: 10,
    fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis',
    background: active ? '#5A4A42' : '#fff', color: active ? '#fff' : '#5A4A42',
    border: `1.5px solid ${active ? '#5A4A42' : '#d5c9c0'}`,
  }),
  attribution: { fontSize: 11, color: '#7b6b61', marginTop: 4, fontStyle: 'italic' },
};

const PILL_NEUTRAL = { bg: '#f2ece5', fg: '#5A4A42' };
const PILL_MONEY = { bg: '#E8F5E9', fg: '#2E7D32' };
const PILL_WARN = { bg: '#FFF4E0', fg: '#8A5A00' };
const PILL_ALERT = { bg: '#FDECEA', fg: '#b71c1c' };
const PILL_INFO = { bg: '#E3F2FD', fg: '#0d47a1' };

export default function DraftOrderReview({ po, storeId: storeIdProp, onClose, onSent, onDeleted, onSaved }) {
  const [deliveryDate, setDeliveryDate] = useState(po.requested_delivery_date || '');
  // Two different things used to share one box. Standing instructions ("leave
  // it on the stairs behind the bar") are true of every delivery this vendor
  // makes and are prefilled from the vendor for whoever orders next; notes
  // ("add two cases, we're catering Saturday") belong to this order alone.
  const [deliveryInstructions, setDeliveryInstructions] = useState(po.delivery_instructions || '');
  const [deliveryNotes, setDeliveryNotes] = useState(po.delivery_notes || '');
  // Who last wrote the standing instructions and when — shown so the next
  // buyer knows whose words they are before they overwrite them.
  const [instructionsAuthor, setInstructionsAuthor] = useState({
    name: po.delivery_instructions_author_name || '',
    at: po.delivery_instructions_updated_at || null,
    id: null,
  });
  // The vendor's stored standing text, so we only write the vendor row back
  // when the buyer actually changed it.
  const vendorInstructionsRef = useRef(po.delivery_instructions || '');
  const [currentUser, setCurrentUser] = useState({ id: null, name: '' });
  const [contactSnapshot, setContactSnapshot] = useState(po.vendor_contact_snapshot || {});
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [editedQtys, setEditedQtys] = useState({});
  // A line the count could not price arrives with unit_price NULL (never 0 —
  // migration 20260821120000). This is where the buyer supplies it: the draft
  // review is the last screen before a vendor sees the order.
  const [editedPrices, setEditedPrices] = useState({});
  const [removedIds, setRemovedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [showVendorContactModal, setShowVendorContactModal] = useState(false);
  // Which field the vendor modal should land on when it opens.
  const [vendorModalFocusField, setVendorModalFocusField] = useState(null);
  // The silent contact resolve below may still be in flight on first paint;
  // the "no email → open the modal" rule waits for it so a vendor that DOES
  // have an email never gets a modal thrown in the buyer's face.
  const [contactResolved, setContactResolved] = useState(!po.vendor_id);
  const autoOpenedEmailRef = useRef(false);
  // True only while the sheet that the review opened for itself is on screen.
  const autoOpenedForThisModalRef = useRef(false);
  // purchase_option_id → the linked vendor product, for ordering-rule context
  // and quantity validation on linked lines.
  const [linkedOptionsById, setLinkedOptionsById] = useState({});
  // The option being edited in the shared OrderingRulesEditor, or null.
  const [orderingRulesOption, setOrderingRulesOption] = useState(null);

  const isAmendment = (po.revision_number || 0) > 0 && !!po.parent_po_id;

  const loadItems = useCallback(async () => {
    setLoadingItems(true);
    const { data } = await supabase
      .from('purchase_order_items')
      .select('id, inventory_item_id, purchase_option_id, description, location, unit, pack_size, snapshot, order_quantity, unit_price, line_total, sort_order')
      .eq('purchase_order_id', po.id)
      .order('sort_order', { ascending: true });
    setItems(data || []);
    setEditedQtys({});
    setEditedPrices({});
    setRemovedIds(new Set());
    setLoadingItems(false);

    // The linked vendor products, for MOQ/increment context and validation.
    // A line keeps purchase_option_id precisely so an edited quantity can be
    // judged against the vendor's real ordering rules; unlinked lines have no
    // rules on record and none are invented for them.
    const linkedIds = [...new Set((data || [])
      .map((row) => row.purchase_option_id)
      .filter((id) => id !== null && id !== undefined))];
    if (linkedIds.length === 0) {
      setLinkedOptionsById({});
    } else {
      try {
        const { data: optionRows, error: optionError } = await supabase
          .from('purchase_options')
          .select('id, vendor_id, vendor_description, purchase_unit, pack_description, qty_per_package, package_price, moq_packages, order_increment, allows_fractional_packages, status')
          .in('id', linkedIds);
        if (optionError) throw optionError;
        const byId = {};
        (optionRows || []).forEach((row) => { byId[String(row.id)] = row; });
        setLinkedOptionsById(byId);
      } catch (optErr) {
        // Non-fatal: without the option rows the client simply cannot
        // pre-validate — the server's send revalidation still applies.
        console.warn('[DraftOrderReview] linked options load failed (non-fatal):', optErr?.message);
        setLinkedOptionsById({});
      }
    }
  }, [po.id]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Track in-progress edits in a ref so the Counter Intelligence refresh below
  // can read the latest value without re-subscribing on every keystroke.
  const hasPendingEditsRef = useRef(false);
  useEffect(() => {
    hasPendingEditsRef.current = Object.keys(editedQtys).length > 0
      || Object.keys(editedPrices).length > 0
      || removedIds.size > 0;
  }, [editedQtys, editedPrices, removedIds]);

  // When Counter Intelligence edits THIS draft from the assistant drawer, reload
  // the line items so the changed quantity/total shows live. Guard hard against
  // discarding the user's own unsaved edits — loadItems() resets editedQtys /
  // removedIds, so we skip the reload entirely while edits are pending and only
  // act when the event targets this order.
  useEffect(() => {
    const onChanged = (e) => {
      const d = e?.detail || {};
      if (e?.type === 'counter-intelligence:applied' && d.kind && d.kind !== 'po_edit') return;
      if (d.poId != null && String(d.poId) !== String(po.id)) return;
      if (hasPendingEditsRef.current) return;
      loadItems();
    };
    window.addEventListener('purchaseOrdersChanged', onChanged);
    window.addEventListener('counter-intelligence:applied', onChanged);
    return () => {
      window.removeEventListener('purchaseOrdersChanged', onChanged);
      window.removeEventListener('counter-intelligence:applied', onChanged);
    };
  }, [loadItems, po.id]);

  // Silently resolve a stale vendor_contact_snapshot on mount.
  // If the snapshot has no email but the PO has a vendor_id, fetch the live
  // vendor contact and update both local state and the DB record so subsequent
  // actions (e.g. clicking "Approve & Send") see the current email without the
  // user having to go and look the vendor up themselves.
  useEffect(() => {
    if (!po.vendor_id) { setContactResolved(true); return undefined; }
    const snap = po.vendor_contact_snapshot || {};
    const snapHasEmail =
      (Array.isArray(snap.orderRecipientEmails) && snap.orderRecipientEmails.length > 0) ||
      !!snap.contactEmail;
    if (snapHasEmail) { setContactResolved(true); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error: vErr } = await supabase
          .from('vendors')
          .select(VENDOR_CONTACT_SELECT)
          .eq('id', po.vendor_id)
          .maybeSingle();
        if (cancelled || vErr || !data) return;
        const fresh = buildVendorContactInfo(data);
        const liveHasEmail = Array.isArray(fresh.orderRecipientEmails) && fresh.orderRecipientEmails.length > 0;
        if (!liveHasEmail) return;
        const newSnapshot = { ...snap, ...fresh };
        setContactSnapshot(newSnapshot);
        try {
          await updateDraftPurchaseOrder(po.id, { vendor_contact_snapshot: newSnapshot });
        } catch (patchErr) {
          console.warn('[DraftOrderReview] Silent snapshot patch failed:', patchErr.message);
        }
      } catch (err) {
        console.warn('[DraftOrderReview] Silent vendor contact resolve failed:', err.message);
      } finally {
        if (!cancelled) setContactResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, [po.vendor_id, po.id, po.vendor_contact_snapshot]);

  // Who is placing this order — needed to sign standing delivery instructions
  // and to tell "you wrote this" from "someone else did".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const user = data?.user;
        if (cancelled || !user) return;
        let name = '';
        try {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('display_name, first_name, last_name')
            .eq('id', user.id)
            .maybeSingle();
          const display = (profile?.display_name || '').trim();
          const first = (profile?.first_name || '').trim();
          const last = (profile?.last_name || '').trim();
          name = display || [first, last].filter(Boolean).join(' ');
        } catch (_) { /* a missing profile is not an error here */ }
        if (!name) name = (user.email || '').split('@')[0] || '';
        if (!cancelled) setCurrentUser({ id: user.id, name });
      } catch (err) {
        console.warn('[DraftOrderReview] current user lookup failed (non-fatal):', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The vendor's standing delivery instructions become this order's default.
  // A draft that already carries its own snapshot keeps it — the buyer may
  // have edited it on a previous visit and that edit is not thrown away.
  useEffect(() => {
    if (!po.vendor_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data, error: vErr } = await supabase
          .from('vendors')
          .select('delivery_instructions, delivery_instructions_author_name, delivery_instructions_author_id, delivery_instructions_updated_at')
          .eq('id', po.vendor_id)
          .maybeSingle();
        // Non-fatal: a store behind the migration simply has no standing
        // instructions yet, and the box stays empty.
        if (cancelled || vErr || !data) return;
        vendorInstructionsRef.current = data.delivery_instructions || '';
        if (!po.delivery_instructions && data.delivery_instructions) {
          setDeliveryInstructions(data.delivery_instructions);
          setInstructionsAuthor({
            name: data.delivery_instructions_author_name || '',
            at: data.delivery_instructions_updated_at || null,
            id: data.delivery_instructions_author_id || null,
          });
        }
      } catch (err) {
        console.warn('[DraftOrderReview] standing delivery instructions unavailable (non-fatal):', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [po.vendor_id, po.delivery_instructions]);

  const getItemQty = (it) => editedQtys[it.id] !== undefined ? editedQtys[it.id] : (parseFloat(it.order_quantity) || 0);
  // NULL is "no price on record", 0 is "free". The edited value keeps that
  // distinction: a blank box is still unpriced, not a zero.
  const getItemPrice = (it) => {
    if (editedPrices[it.id] !== undefined) {
      const parsed = parseFloat(editedPrices[it.id]);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    }
    const stored = parseFloat(it.unit_price);
    return Number.isFinite(stored) ? stored : null;
  };
  const isUnpriced = (it) => getItemPrice(it) === null && !resolvePoLineBilling(it).isEstimated;
  // ORDER is not BILLING. A catch-weight line orders a CASE and is invoiced
  // per POUND, so quantity × unit_price is not its total — for the ribeye it
  // produced $16.40 for a case that will invoice at about $984. buildPoLineDisplay
  // reads the billing facts frozen on the line and returns both readings, with
  // the estimate marked; a fixed-package line comes back exactly as before.
  const getItemDisplay = (it) => buildPoLineDisplay(
    { ...it, unit_price: getItemPrice(it) },
    { quantity: getItemQty(it) },
  );
  const getItemTotal = (it) => {
    const billing = resolvePoLineBilling(it);
    if (billing.isEstimated) {
      return getItemQty(it) * billing.estimatedQtyPerOrderUnit * billing.billingUnitPrice;
    }
    const price = getItemPrice(it);
    return price === null ? null : getItemQty(it) * price;
  };
  const visibleItems = items.filter((it) => !removedIds.has(it.id));
  // An unpriced line adds nothing rather than pulling the total down to a
  // figure that reads as complete; the count of them is stated beside it.
  const total = visibleItems.reduce((s, it) => s + (getItemTotal(it) ?? 0), 0);
  const unpricedCount = visibleItems.filter(isUnpriced).length;
  const totalCount = visibleItems.length;
  // Any estimated line makes the grand total an estimate too. The order can
  // still be sent — the vendor knows what a catch-weight case is — but the
  // screen may not present the number as if it were settled.
  const hasEstimatedLines = visibleItems.some((it) => resolvePoLineBilling(it).isEstimated);

  // Shared eligibility check — uses live contactSnapshot state (may differ from
  // po.vendor_contact_snapshot after a vendor edit) so the send button reacts
  // to in-session changes.
  const {
    canSend: canSendDraft,
    hasEmail,
    hasItems: snapshotHasItems,
    hasVendor,
    recipients,
    ruleViolations,
    blockerReason: sendBlockerReason,
  } = computeDraftSendEligibility({
    vendorContactSnapshot: contactSnapshot,
    vendorNameSnapshot: po.vendor_name_snapshot,
    vendorId: po.vendor_id,
    lineCount: visibleItems.length,
    items: visibleItems,
    // Judge the quantity the buyer SEES (their unsaved edit), not the stored
    // one — a stale legal value must not mask an illegal edit or vice versa.
    optionsById: linkedOptionsById,
    quantityOf: getItemQty,
    // The price the buyer SEES, including an unsaved edit — the same rule the
    // quantity gate already follows.
    priceOf: getItemPrice,
  });
  const violationByItemId = {};
  (ruleViolations || []).forEach((violation) => { violationByItemId[violation.id] = violation; });

  // No email on file is not a warning, it is a dead end: this order cannot be
  // sent until someone types one. So the review opens ON the email box rather
  // than showing a red panel and waiting to be noticed. Once per open — a
  // buyer who dismisses the modal to check the lines is not re-interrupted.
  useEffect(() => {
    if (autoOpenedEmailRef.current) return;
    if (!contactResolved || hasEmail) return;
    autoOpenedEmailRef.current = true;
    autoOpenedForThisModalRef.current = true;
    setVendorModalFocusField('contact_email');
    setShowVendorContactModal(true);
  }, [contactResolved, hasEmail]);

  const openVendorSettings = (focusField = null) => {
    autoOpenedForThisModalRef.current = false;
    setVendorModalFocusField(focusField);
    setShowVendorContactModal(true);
  };

  const handleQtyChange = (id, val) => {
    const q = parseFloat(val);
    setEditedQtys((prev) => ({ ...prev, [id]: isNaN(q) ? 0 : q }));
  };

  // Blank stays blank. Clearing the box means "still no price", not "$0.00" —
  // the two are different answers and the send gate treats them differently.
  const handlePriceChange = (id, val) => {
    setEditedPrices((prev) => ({ ...prev, [id]: val }));
  };

  const handleRemoveItem = (id) => setRemovedIds((prev) => new Set([...prev, id]));

  /**
   * Items that can go on this vendor's order.
   *
   * `inventory_items.vendor_id` names ONE vendor, which was the whole truth
   * when an item had one supplier. An item bought from four is still stamped
   * with whichever one happened to create it, so filtering on that column
   * alone hides the item from the other three vendors' orders — you cannot add
   * chocolate milk to a Sysco order because the row says Costco.
   *
   * So the list is a union: items stamped with this vendor, PLUS items with an
   * active purchase option from this vendor. Additive by construction — every
   * item that appears today still appears, and the ones that were unreachable
   * now appear too.
   */
  const handleOpenItemSearch = async () => {
    setShowItemSearch(true);
    if (inventoryItems.length > 0) return;
    setInventoryLoading(true);
    try {
      const columns = 'id, description, location, unit, unit_price, vendor_id';
      const legacy = supabase
        .from('inventory_items')
        .select(columns)
        .eq('store_id', po.store_id)
        .eq('vendor_id', po.vendor_id)
        // Exclude soft-deleted/archived items so a deleted item can't be re-added to an order.
        .not('is_active', 'is', false)
        .order('description');

      // Non-fatal: a store without purchase_options gets exactly the legacy
      // list, which is what it had before.
      const viaOptions = supabase
        .from('purchase_options')
        .select(`id, inventory_item_id, purchase_unit, pack_description, qty_per_package, package_price, item:inventory_items!inner(${columns}, is_active, store_id)`)
        .eq('store_id', po.store_id)
        .eq('vendor_id', po.vendor_id)
        .eq('status', 'active');

      const [legacyResult, optionResult] = await Promise.all([legacy, viaOptions]);
      if (optionResult?.error) {
        console.warn('[DraftOrderReview] purchase options unavailable (non-fatal):', optionResult.error.message);
      }

      const byId = new Map();
      (legacyResult?.data || []).forEach((row) => byId.set(String(row.id), row));
      (optionResult?.data || []).forEach((row) => {
        const item = row.item;
        if (!item || item.is_active === false) return;
        if (String(item.store_id) !== String(po.store_id)) return;
        const key = String(item.id);
        // The vendor's own package facts ride along so the row can say what an
        // order of "1" actually buys.
        const packageInfo = {
          purchase_unit: row.purchase_unit || null,
          pack_description: row.pack_description || null,
          qty_per_package: row.qty_per_package ?? null,
          package_price: row.package_price ?? null,
          purchase_option_id: row.id || null,
        };
        byId.set(key, { ...(byId.get(key) || item), ...packageInfo });
      });

      setInventoryItems(
        [...byId.values()].sort((a, b) => String(a.description || '').localeCompare(String(b.description || ''))),
      );
    } catch (e) {
      console.warn('[DraftOrderReview] inventory load failed:', e.message);
    } finally {
      setInventoryLoading(false);
    }
  };

  const handleAddInventoryItem = async (invItem) => {
    try {
      // Persist any pending qty edits / removals first so the upcoming
      // loadItems() reload doesn't discard the user's in-progress changes.
      if (Object.keys(editedQtys).length > 0 || Object.keys(editedPrices).length > 0 || removedIds.size > 0) {
        await updateDraftItems(po.id, { items, editedQtys, editedPrices, removedIds });
      }
      await addDraftItem(po.id, invItem, items.length + 1);
      await loadItems();
      setShowItemSearch(false);
      setItemSearchTerm('');
    } catch (err) {
      setError(err.message || 'Add item failed.');
    }
  };

  const filteredInventoryItems = inventoryItems.filter((it) =>
    !itemSearchTerm || it.description?.toLowerCase().includes(itemSearchTerm.toLowerCase()) || it.location?.toLowerCase().includes(itemSearchTerm.toLowerCase())
  );

  const persistItemChanges = async () => {
    await updateDraftItems(po.id, { items, editedQtys, editedPrices, removedIds });
  };

  const handleRefreshVendor = async (vendorIdOverride = null) => {
    setRefreshing(true); setError(''); setSuccess('');
    try {
      // Try by vendor_id first when one is recorded on the PO. An explicit
      // override is accepted so callers that just resolved a vendor id (e.g.
      // handleVendorContactSaved) can bypass the stale render-time closure.
      // Otherwise fall back to a name lookup against the live `vendors` table.
      //
      // Only a scalar counts as an override. Wired straight to an onClick this
      // used to receive the React event object, which PostgREST stringified
      // into `id=eq.[object Object]` and the database rejected as
      // `invalid input syntax for type uuid` right at the top of the review.
      let vendor = null;
      const overrideIsScalar = vendorIdOverride != null
        && (typeof vendorIdOverride === 'string' || typeof vendorIdOverride === 'number');
      const effectiveVendorId = (overrideIsScalar ? vendorIdOverride : null) || po.vendor_id;
      if (effectiveVendorId) {
        const { data, error: vErr } = await supabase
          .from('vendors')
          .select(VENDOR_CONTACT_SELECT)
          .eq('id', effectiveVendorId)
          .maybeSingle();
        if (vErr) throw vErr;
        vendor = data;
      }

      const snapshotName = (po.vendor_name_snapshot || '').trim();
      if (!vendor && snapshotName) {
        const normalized = normalizeVendorNameForLookup(snapshotName);
        if (normalized && po.store_id != null) {
          const { data, error: vErr } = await supabase
            .from('vendors')
            .select(VENDOR_CONTACT_SELECT)
            .eq('store_id', po.store_id)
            .eq('normalized_display_name', normalized);
          if (vErr) throw vErr;
          // The `(store_id, normalized_display_name)` partial unique
          // index excludes merged vendors, but we filter again here in
          // case the index has not yet been migrated on this database.
          const candidates = (data || []).filter((row) => String(row?.status || '').toLowerCase() !== 'merged');
          vendor = candidates[0] || null;
        }
      }

      if (!vendor) {
        throw new Error(
          snapshotName
            ? `No vendor matches "${snapshotName}" for this store. Open the Vendor Editor to create or rename one, then try again.`
            : 'This draft has no vendor name to match against. Open the Vendor Editor and assign a vendor.'
        );
      }

      const fresh = buildVendorContactInfo(vendor);
      const newSnapshot = { ...contactSnapshot, ...fresh };
      const patch = { vendor_contact_snapshot: newSnapshot };
      const newVendorId = vendor.id;
      const vendorIdChanged = newVendorId && newVendorId !== po.vendor_id;
      if (vendorIdChanged) patch.vendor_id = newVendorId;

      await updateDraftPurchaseOrder(po.id, patch);
      setContactSnapshot(newSnapshot);
      if (vendorIdChanged && onSaved) {
        // Bubble the vendor_id change up so the parent re-renders this
        // sheet with the rescued id and subsequent actions (e.g. adding
        // an inventory item, sending the draft) see it too.
        onSaved({ ...po, vendor_id: newVendorId, vendor_contact_snapshot: newSnapshot });
      }
      setSuccess('Vendor contact refreshed.');
      setRefreshing(false);
      return newSnapshot;
    } catch (err) {
      setError(err.message || 'Failed to refresh vendor contact.');
    }
    setRefreshing(false);
    return null;
  };

  /**
   * Standing instructions belong to the VENDOR, so an edit here becomes the
   * default for the next order to them — signed with who changed it and when.
   * Called on save and on send; a no-op when the text is unchanged, so
   * re-saving a draft never restamps someone else's authorship as yours.
   * Best-effort: the order itself keeps its own snapshot either way.
   */
  const persistStandingInstructions = async () => {
    const next = (deliveryInstructions || '').trim();
    const stored = (vendorInstructionsRef.current || '').trim();
    if (!po.vendor_id || next === stored) return null;
    const stampedAt = new Date().toISOString();
    const authorName = currentUser.name || null;
    try {
      const { error: vErr } = await supabase
        .from('vendors')
        .update({
          delivery_instructions: next || null,
          delivery_instructions_author_name: authorName,
          delivery_instructions_author_id: currentUser.id || null,
          delivery_instructions_updated_at: stampedAt,
        })
        .eq('id', po.vendor_id);
      if (vErr) throw vErr;
      vendorInstructionsRef.current = next;
      setInstructionsAuthor({ name: authorName || '', at: stampedAt, id: currentUser.id || null });
      return { authorName, stampedAt };
    } catch (err) {
      // A store behind the migration has no column to write to. The order's
      // own copy still saves; only the "next time" default is skipped.
      console.warn('[DraftOrderReview] standing instructions not saved (non-fatal):', err?.message);
      return null;
    }
  };

  // What rides on the PO itself. Nothing typed into this screen is thrown
  // away: the one-off note AND a frozen copy of the standing instructions are
  // stored on the order, so a sent PO still reads the way it was sent even
  // after the vendor's standing text is rewritten.
  // `stamp` is what persistStandingInstructions just wrote, passed in rather
  // than read back from state: setInstructionsAuthor has not re-rendered yet in
  // the same tick, so reading state here would credit the PREVIOUS author for
  // text this buyer just typed.
  const deliveryPatch = (stamp = null) => {
    const hasInstructions = !!(deliveryInstructions || '').trim();
    return {
      requested_delivery_date: deliveryDate || null,
      delivery_notes: deliveryNotes || null,
      delivery_instructions: deliveryInstructions || null,
      delivery_instructions_author_name: hasInstructions
        ? (stamp?.authorName || instructionsAuthor.name || currentUser.name || null)
        : null,
      delivery_instructions_updated_at: hasInstructions
        ? (stamp?.stampedAt || instructionsAuthor.at || new Date().toISOString())
        : null,
    };
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await persistItemChanges();
      const stamp = await persistStandingInstructions();
      const patch = deliveryPatch(stamp);
      await updateDraftPurchaseOrder(po.id, patch);
      setSuccess('Draft saved.');
      if (onSaved) onSaved({ ...po, ...patch });
      await loadItems();
    } catch (err) {
      setError(err.message || 'Save failed.');
    }
    setSaving(false);
  };

  const handleSend = async () => {
    if (!canSendDraft) { setError(sendBlockerReason || 'Cannot send this draft. Check vendor, email, and items.'); return; }
    setSending(true); setError(''); setSuccess('');
    try {
      await persistItemChanges();
      const stamp = await persistStandingInstructions();
      // Save the delivery block onto the order before dispatching it. The send
      // call carries the three text fields itself, but not their authorship —
      // and nothing typed on this screen is allowed to be discarded.
      try {
        await updateDraftPurchaseOrder(po.id, deliveryPatch(stamp));
      } catch (patchErr) {
        // Never block a send on the provenance write; the send patches the
        // text fields anyway.
        console.warn('[DraftOrderReview] delivery block not saved before send:', patchErr?.message);
      }
      const result = await sendDraftPurchaseOrder(po.id, {
        requestedDeliveryDate: deliveryDate || null,
        deliveryNotes: deliveryNotes || null,
        deliveryInstructions: deliveryInstructions || null,
      });
      setShowSendConfirm(false);
      setSuccess(`PO sent! (${result.poNumber || po.po_number})`);
      setTimeout(() => { if (onSent) onSent(result); }, 800);
    } catch (err) {
      setShowSendConfirm(false);
      setError(err.message || 'Send failed.');
    }
    setSending(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true); setError('');
    try {
      await deleteDraftPurchaseOrder(po.id);
      if (onDeleted) onDeleted(po.id);
    } catch (err) {
      setError(err.message || 'Delete failed.');
      setDeleting(false);
    }
  };

  const handleVendorContactSaved = async (updatedVendorRow, resolvedVendorId) => {
    // The review opened this sheet by itself because the order was unsendable.
    // Fixing that returns the buyer to the order to review it — it does not
    // shove a "Send this now?" confirmation in front of lines nobody has read.
    const wasAutoOpened = autoOpenedForThisModalRef.current;
    autoOpenedForThisModalRef.current = false;
    setShowVendorContactModal(false);
    setVendorModalFocusField(null);
    setError('');

    const effectiveVendorId = resolvedVendorId || po.vendor_id;
    const needsVendorIdUpdate = resolvedVendorId && po.vendor_id !== resolvedVendorId;

    // Fast path: the modal returns the full updated vendor row from the RPC result.
    // The vendor email is already saved on the server — update local state immediately
    // so the UI reflects the change regardless of whether the draft PO patch succeeds.
    if (updatedVendorRow && typeof updatedVendorRow === 'object' && updatedVendorRow.id) {
      const fresh = buildVendorContactInfo(updatedVendorRow);
      setContactSnapshot(fresh);
      const hasEmailNow = Array.isArray(fresh.orderRecipientEmails) && fresh.orderRecipientEmails.length > 0;

      // Persist the snapshot to the draft PO record (best-effort — vendor save already
      // succeeded server-side so this is a local sync step only).
      try {
        const patch = { vendor_contact_snapshot: fresh };
        if (needsVendorIdUpdate) patch.vendor_id = resolvedVendorId;
        await updateDraftPurchaseOrder(po.id, patch);
        if (onSaved) onSaved({ ...po, vendor_id: effectiveVendorId, vendor_contact_snapshot: fresh });
      } catch (err) {
        // Snapshot persistence failed — the vendor email IS saved; the draft
        // will pick it up when it is next opened.
        setError(
          'Email saved, but could not update this draft — close and reopen this draft if the Send button stays disabled.'
        );
        console.warn('[DraftOrderReview] Fast-path draft patch failed:', err.message);
      }

      // Auto-advance: local state is already updated, so open send confirm now.
      if (hasEmailNow && !wasAutoOpened) setShowSendConfirm(true);
      return;
    }

    // Slow-path fallback (no vendor row returned): persist vendor_id if needed,
    // then do a full vendor refresh.
    if (needsVendorIdUpdate) {
      try {
        await updateDraftPurchaseOrder(po.id, { vendor_id: resolvedVendorId });
        if (onSaved) onSaved({ ...po, vendor_id: resolvedVendorId });
      } catch (err) {
        console.warn('[DraftOrderReview] Could not persist resolved vendor_id:', err.message);
      }
    }
    const refreshedSnapshot = await handleRefreshVendor(resolvedVendorId || null);
    if (refreshedSnapshot) {
      const hasEmailNow = Array.isArray(refreshedSnapshot.orderRecipientEmails) && refreshedSnapshot.orderRecipientEmails.length > 0;
      if (hasEmailNow && !wasAutoOpened) setShowSendConfirm(true);
    } else if (!refreshedSnapshot) {
      // handleRefreshVendor already called setError; add context about what triggered it.
      setError((prev) =>
        prev
          ? `Email saved but vendor contact could not be refreshed — ${prev}`
          : 'Email saved but vendor contact could not be refreshed. Close and reopen this draft to retry.'
      );
    }
  };

  const isBusy = saving || sending || deleting || refreshing;

  // Recomputed each render so a review left open across midnight does not go
  // on offering yesterday as "Today".
  const dayPresets = buildDeliveryDatePresets();

  // Whose words these are. "You added this" reads differently from "Marco
  // added this" when you are about to overwrite them.
  const instructionsAttribution = (() => {
    if (!(deliveryInstructions || '').trim()) return '';
    if (!instructionsAuthor.name && !instructionsAuthor.at) return '';
    const mine = instructionsAuthor.id && currentUser.id && String(instructionsAuthor.id) === String(currentUser.id);
    const who = mine ? 'You added this' : `Added by ${instructionsAuthor.name || 'a teammate'}`;
    const when = instructionsAuthor.at ? formatLocalDateTime(instructionsAuthor.at) : '';
    return when ? `${who} · ${when}` : who;
  })();

  useHideMobileNav(true);

  return (
    <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        ${FOCUS_RING_CSS}
        @media (max-width: 640px) {
          .draft-review-footer {
            position: sticky !important;
            bottom: 0 !important;
            flex-direction: row !important;
            flex-wrap: wrap !important;
          }
          .draft-review-footer button {
            min-height: 44px !important;
          }
          .draft-review-table-wrap {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }
        }
      `}</style>
      <div style={S.sheet}>
        <div style={S.header}>
          <div>
            <div style={S.title}>
              {isAmendment && <span style={{ color: '#bf360c', marginRight: 8, fontSize: 13, verticalAlign: 'middle', background: '#fff3cd', padding: '2px 6px', borderRadius: 4 }}>AMENDMENT</span>}
              {po.vendor_name_snapshot || 'Draft PO'}
            </div>
            <div style={S.subtitle}>
              {isAmendment
                ? `Amendment draft · Revision ${po.revision_number}`
                : 'New purchase order draft'}
            </div>
            {/* Everything the order IS, as pills — the same chips the shopping
                review uses, so the two screens read as one system and the
                numbers can be found without reading a sentence. */}
            <div style={S.pillRow} data-testid="draft-summary-pills">
              <span style={S.pill(PILL_NEUTRAL)}>{loadingItems ? '… items' : `${totalCount} ${totalCount === 1 ? 'item' : 'items'}`}</span>
              <span style={S.pill(unpricedCount > 0 || hasEstimatedLines ? PILL_WARN : PILL_MONEY)}>
                {hasEstimatedLines || unpricedCount > 0 ? `~${formatPoCurrency(total)}` : formatPoCurrency(total)}
              </span>
              {unpricedCount > 0 && (
                <span style={S.pill(PILL_ALERT)}>{`${unpricedCount} unpriced`}</span>
              )}
              <span style={S.pill(deliveryDate ? PILL_INFO : PILL_NEUTRAL)}>
                {deliveryDate ? describeDeliveryDate(deliveryDate) : 'No delivery date'}
              </span>
              <span style={S.pill(hasEmail ? PILL_NEUTRAL : PILL_ALERT)}>
                {hasEmail ? `To ${recipients.length} ${recipients.length === 1 ? 'address' : 'addresses'}` : 'No vendor email'}
              </span>
              <span style={S.pill(PILL_NEUTRAL)}>{`Started ${formatLocalDateTime(po.created_at)}`}</span>
              {po.ordered_by_name && <span style={S.pill(PILL_NEUTRAL)}>{`By ${po.ordered_by_name}`}</span>}
            </div>
          </div>
          <button style={S.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={S.body}>
          {isAmendment && (
            <div style={{ ...S.alert, ...S.alertAmended }}>
              <strong>Amendment Draft</strong> — This is a revision of a previously sent PO. Sending it will notify the vendor of the updated order.
            </div>
          )}
          {error && <div style={{ ...S.alert, ...S.alertError }}>{error}</div>}
          {success && <div style={{ ...S.alert, ...S.alertSuccess }}>{success}</div>}

          <div style={S.section}>
            <div style={S.sectionLabel}>Vendor</div>
            <div style={{ fontSize: 13, color: '#2b1d14', marginBottom: 4 }}>
              <strong>{po.vendor_name_snapshot || '—'}</strong>
              {contactSnapshot.contactName ? <span style={{ color: '#7b6b61' }}> · {contactSnapshot.contactName}</span> : null}
            </div>
            <div style={{ fontSize: 12, color: '#7b6b61', marginBottom: 6 }}>Will be sent to:</div>
            {hasEmail ? (
              /* One pill per recipient. Two addresses used to run together in
                 a comma-separated line; as pills you can see at a glance that
                 the order goes to two people. */
              <div style={{ ...S.pillRow, marginTop: 0 }} data-testid="draft-recipient-pills">
                {recipients.map((email) => (
                  <span key={email} style={S.pill(PILL_INFO)}>✉ {email}</span>
                ))}
              </div>
            ) : (
              <div style={S.noEmail} data-testid="draft-no-email">
                No email on file — the order cannot be sent until a vendor email is set.
              </div>
            )}
            {/* One button, whether or not there is an email yet: the vendor's
                whole settings sheet, opened on the field that is in the way. */}
            <button
              type="button"
              data-testid="draft-edit-vendor-contact"
              style={{ ...S.btnSecondary, fontSize: 13, marginTop: 8 }}
              onClick={() => openVendorSettings(hasEmail ? null : 'contact_email')}
            >
              Edit Contact Information
            </button>
          </div>

          <div style={S.section}>
            <div style={S.sectionLabel}>Delivery Details</div>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label} htmlFor="draftDeliveryDate">Requested Delivery Date</label>
              {/* Restaurant people say "Wednesday", not "the 26th". The next
                  seven days are buttons — big enough for a thumb, abbreviated
                  so they stay big — and the calendar underneath covers
                  anything further out. Tapping the chosen day again clears it,
                  because "no date requested" is a real answer. */}
              <div data-testid="draft-delivery-day-presets">
                {[
                  { style: S.dayRowNear, presets: dayPresets.slice(0, 2) },
                  { style: S.dayRowWeek, presets: dayPresets.slice(2) },
                ].map((row, rowIndex) => (
                  <div key={rowIndex} style={row.style}>
                    {row.presets.map((preset) => {
                      const active = deliveryDate === preset.value;
                      return (
                        <button
                          key={preset.value}
                          type="button"
                          style={S.dayBtn(active)}
                          aria-pressed={active}
                          title={preset.fullLabel}
                          onClick={() => setDeliveryDate(active ? '' : preset.value)}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
              <input
                id="draftDeliveryDate"
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                style={{ ...S.input, marginTop: 8 }}
                aria-label="Requested delivery date"
              />
              <div style={S.helper}>
                {deliveryDate
                  ? `Requested for ${describeDeliveryDate(deliveryDate)}.`
                  : 'Optional — pick a day above, or any date from the calendar.'}
              </div>
            </div>

            {/* Two boxes, because two different things were being typed into
                one. The top one outlives the order; the bottom one does not. */}
            <div style={{ marginBottom: 14 }}>
              <label style={S.label} htmlFor="draftDeliveryInstructions">Delivery Instructions</label>
              <textarea
                id="draftDeliveryInstructions"
                data-testid="draft-delivery-instructions"
                value={deliveryInstructions}
                onChange={(e) => setDeliveryInstructions(e.target.value)}
                placeholder="e.g. Leave on the stairs behind the bar"
                style={S.textarea}
              />
              <div style={S.helper}>
                Saved for {po.vendor_name_snapshot || 'this vendor'} — it comes back already filled in on the next order.
              </div>
              {instructionsAttribution && (
                <div style={S.attribution} data-testid="draft-instructions-attribution">{instructionsAttribution}</div>
              )}
            </div>

            <div>
              <label style={S.label} htmlFor="draftDeliveryNotes">Delivery Notes</label>
              <textarea
                id="draftDeliveryNotes"
                data-testid="draft-delivery-notes"
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                placeholder="e.g. Add two extra cases — catering Saturday"
                style={S.textarea}
              />
              <div style={S.helper}>This order only. Sent with the order and kept on it, but never carried to the next one.</div>
            </div>
          </div>

          <div style={S.section}>
            <div style={S.sectionLabel}>Order Items ({loadingItems ? '…' : totalCount})</div>
            {loadingItems ? (
              <div style={{ color: '#7b6b61', fontSize: 13, padding: 8 }}>Loading items…</div>
            ) : (
              <>
                <div className="draft-review-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ ...S.table, tableLayout: 'fixed', minWidth: 360 }}>
                  <colgroup>
                    <col style={{ width: '44%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '6%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={S.th}>Item</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Qty</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Unit Price</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                      <th style={{ ...S.th, width: 28 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((it) => {
                      // ORDER vs BILLING, per line. A catch-weight line reads
                      // "1 case · 4 PC / AVG 60 LB · $16.40/LB · ~60 LB ·
                      // ~$984.00"; a fixed-package line reads exactly as it
                      // always has.
                      const d = getItemDisplay(it);
                      const unitAndPack = formatPoOrderUnit(it.unit, resolvePoLinePackSize(it));
                      const linkedOption = it.purchase_option_id != null
                        ? linkedOptionsById[String(it.purchase_option_id)] || null
                        : null;
                      const violation = violationByItemId[it.id] || null;
                      const optionUnit = String(linkedOption?.purchase_unit || it.unit || 'package').trim() || 'package';
                      const optionUnitPlural = optionUnit.endsWith('s') ? optionUnit : `${optionUnit}s`;
                      return (
                      <tr key={it.id}>
                        {/* Order unit and pack under the name rather than in
                            columns of their own — this table is 360px wide on
                            a phone, and "1" with no idea what one is is the
                            thing being fixed. */}
                        <td style={S.td}>
                          <div>{it.description || '—'}</div>
                          {unitAndPack && (
                            <div style={{ color: '#7b6b61', fontSize: 11.5, marginTop: 2 }}>
                              {unitAndPack}
                              {d.isEstimated && d.estWeight ? ` · ${d.estWeight}` : ''}
                            </div>
                          )}
                          {d.countedNeed && (
                            <div style={{ color: '#526B7C', fontSize: 11.5, marginTop: 3 }} data-testid="draft-line-counted-need">
                              Counted result: Need {d.countedNeed}
                            </div>
                          )}
                          {d.delivers && (
                            <div style={{ color: '#526B7C', fontSize: 11.5, marginTop: 2 }} data-testid="draft-line-delivery">
                              Vendor order delivers {d.delivers}
                              {d.abovePar ? ` · ${d.abovePar} above PAR` : ''}
                            </div>
                          )}
                          {/* The vendor product's ordering rules, right where
                              the quantity is edited: the vendor minimum and
                              the steps this product is ordered in, with the
                              one place to change them. A line with no linked
                              vendor product says so instead of inventing a
                              rule. */}
                          {linkedOption ? (
                            <div style={{ color: '#7b6b61', fontSize: 11, marginTop: 2 }} data-testid="draft-line-ordering-rules">
                              {Number(linkedOption.moq_packages) > 0
                                ? `Vendor minimum ${linkedOption.moq_packages} ${Number(linkedOption.moq_packages) === 1 ? optionUnit : optionUnitPlural}`
                                : 'No stated vendor minimum'}
                              {' · '}
                              {Number(linkedOption.order_increment) > 0 && Number(linkedOption.order_increment) !== 1
                                ? `ordered in ${linkedOption.order_increment} ${optionUnitPlural} steps`
                                : linkedOption.allows_fractional_packages === true
                                  ? 'exact quantity (cut to order)'
                                  : `ordered in whole ${optionUnitPlural}`}
                              {' · '}
                              <button
                                type="button"
                                data-testid="draft-line-edit-ordering-rules"
                                style={{ background: 'none', border: 'none', padding: 0, color: '#5A4A42', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}
                                onClick={() => setOrderingRulesOption(linkedOption)}
                              >
                                Edit ordering rules
                              </button>
                            </div>
                          ) : (
                            <div style={{ color: '#9b8d84', fontSize: 11, marginTop: 2, fontStyle: 'italic' }} data-testid="draft-line-manual">
                              Manual line — vendor ordering rules unavailable
                            </div>
                          )}
                          {violation && (
                            <div style={{ color: '#b71c1c', fontSize: 11, marginTop: 2, fontWeight: 600 }} data-testid="draft-line-rule-violation">
                              This quantity {violation.message}.
                            </div>
                          )}
                          {d.isEstimated && (
                            <div style={{ color: '#7b4f00', fontSize: 11, marginTop: 2, fontStyle: 'italic' }}>
                              Estimated — final invoice varies with shipped weight
                            </div>
                          )}
                        </td>
                        <td style={S.tdNum}>
                          <input
                            type="number" min="0" step="any" value={getItemQty(it)}
                            onChange={(e) => handleQtyChange(it.id, e.target.value)}
                            style={{ ...S.qtyInput, ...(violation ? { borderColor: '#ef9a9a', background: '#fdecea' } : {}) }}
                          />
                          {/* What is being ordered. On a catch-weight line the
                              number is CASES, and the pound is the price basis
                              in the next column — never the order quantity. */}
                          {d.orderUnit && (
                            <div style={{ color: '#7b6b61', fontSize: 10.5, marginTop: 2 }}>{d.orderUnit}</div>
                          )}
                        </td>
                        <td style={S.tdNum}>
                          {d.isEstimated ? d.priceBasis : (
                            <>
                              <input
                                type="number" min="0" step="0.01"
                                value={editedPrices[it.id] !== undefined
                                  ? editedPrices[it.id]
                                  : (getItemPrice(it) === null ? '' : String(getItemPrice(it)))}
                                placeholder={isUnpriced(it) ? 'Price' : ''}
                                onChange={(e) => handlePriceChange(it.id, e.target.value)}
                                data-testid="draft-line-price"
                                style={{
                                  ...S.qtyInput,
                                  ...(isUnpriced(it) ? { borderColor: '#d9a44a', background: '#fff8ec' } : {}),
                                }}
                              />
                              {isUnpriced(it) && (
                                <div style={{ color: '#8a5a2b', fontSize: 10.5, marginTop: 2, fontWeight: 600 }} data-testid="draft-line-price-missing">
                                  {`Price needed per ${it.unit || 'order unit'}`}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        <td style={S.tdNum}>
                          {d.isEstimated
                            ? d.estTotal
                            : (getItemTotal(it) === null ? 'Price needed' : formatPoCurrency(getItemTotal(it)))}
                        </td>
                        <td style={{ ...S.td, textAlign: 'center', padding: '7px 4px' }}>
                          <button style={S.removeBtn} onClick={() => handleRemoveItem(it.id)} title="Remove item">×</button>
                        </td>
                      </tr>
                      );
                    })}
                    {removedIds.size > 0 && (
                      <tr>
                        <td colSpan={5} style={{ ...S.td, fontSize: 11, color: '#b71c1c', fontStyle: 'italic' }}>
                          {removedIds.size} item{removedIds.size !== 1 ? 's' : ''} removed — {isAmendment ? 'send' : 'save or send'} to confirm
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={3} style={{ ...S.td, textAlign: 'right', fontWeight: 700, background: '#f7f1ea' }}>
                        {hasEstimatedLines ? 'Estimated total' : 'Total'}
                      </td>
                      {/* One estimated line makes the whole total an estimate.
                          Printing it bare would be the same mistake one row up:
                          a number that reads as settled when it is not. */}
                      <td style={{ ...S.tdNum, fontWeight: 700, background: '#f7f1ea' }}>
                        {hasEstimatedLines || unpricedCount > 0 ? `~${formatPoCurrency(total)}` : formatPoCurrency(total)}
                        {unpricedCount > 0 && (
                          <div style={{ color: '#8a5a2b', fontSize: 10.5, fontWeight: 600 }}>
                            {`${unpricedCount} line${unpricedCount === 1 ? '' : 's'} unpriced`}
                          </div>
                        )}
                      </td>
                      <td style={{ background: '#f7f1ea', borderBottom: '1px solid #f0ebe4' }}></td>
                    </tr>
                    {hasEstimatedLines && (
                      <tr>
                        <td colSpan={5} style={{ ...S.td, fontSize: 11, color: '#7b4f00', background: '#fff3cd' }}>
                          This order contains catch-weight products, which are ordered by the case
                          and billed by weight. The total above is an estimate — the final invoice
                          varies with the weight actually shipped.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
                {showItemSearch ? (
                  <div style={{ marginTop: 8, border: '1px solid #d9cfc7', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ background: '#f7f1ea', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="text"
                        autoFocus
                        value={itemSearchTerm}
                        onChange={(e) => setItemSearchTerm(e.target.value)}
                        placeholder="Search inventory items…"
                        style={{ flex: 1, border: '1px solid #d9cfc7', borderRadius: 6, padding: '6px 10px', fontSize: 13, color: '#2b1d14', background: '#fff' }}
                      />
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7b6b61', fontSize: 16, lineHeight: 1 }} onClick={() => { setShowItemSearch(false); setItemSearchTerm(''); }}>×</button>
                    </div>
                    <div style={{ maxHeight: 180, overflowY: 'auto', background: '#fff' }}>
                      {inventoryLoading ? (
                        <div style={{ padding: '10px 12px', color: '#7b6b61', fontSize: 13 }}>Loading inventory…</div>
                      ) : filteredInventoryItems.length === 0 ? (
                        <div style={{ padding: '10px 12px', color: '#7b6b61', fontSize: 13 }}>{inventoryItems.length === 0 ? 'No items are linked to this vendor yet. Add a purchase option for them on the item, then they can go on this order.' : 'No items match your search.'}</div>
                      ) : filteredInventoryItems.slice(0, 30).map((it) => (
                        <div key={it.id} onClick={() => handleAddInventoryItem(it)} style={{ padding: '8px 12px', borderBottom: '1px solid #f0ebe4', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }} onMouseEnter={(e) => (e.currentTarget.style.background = '#f7f1ea')} onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
                          <div style={{ minWidth: 0 }}>
                            <span style={{ fontWeight: 600, color: '#2b1d14' }}>{it.description}</span>
                            {/* What one of this vendor's packages actually is.
                                Without it, adding "1" of an item is a quantity
                                with no unit — one case or one bottle. */}
                            {it.purchase_unit && (
                              <div style={{ color: '#7b6b61', fontSize: 11.5 }}>
                                1 {formatPoOrderUnit(it.purchase_unit, it.pack_description)}
                                {it.qty_per_package && it.unit ? ` = ${it.qty_per_package} ${it.unit}` : ''}
                              </div>
                            )}
                          </div>
                          <span style={{ color: '#5A4A42', fontSize: 12, whiteSpace: 'nowrap' }}>
                            {it.package_price
                              ? `${formatPoCurrency(parseFloat(it.package_price))}/${it.purchase_unit || 'package'}`
                              : `${it.unit_price ? formatPoCurrency(parseFloat(it.unit_price)) : ''}${it.unit ? `/${it.unit}` : ''}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button style={{ marginTop: 8, background: 'none', border: '1.5px dashed #d5c9c0', borderRadius: 8, padding: '6px 14px', color: '#5A4A42', fontWeight: 600, fontSize: 13, cursor: 'pointer', width: '100%' }} onClick={handleOpenItemSearch}>
                    + Add item from inventory
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div style={S.footer} className="draft-review-footer">
          {confirmDelete ? (
            <>
              <span style={{ fontSize: 13, color: '#b71c1c', alignSelf: 'center', fontWeight: 600 }}>Delete this draft?</span>
              <button style={S.btnDanger} onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Yes, delete'}</button>
              <button style={S.btnSecondary} onClick={() => setConfirmDelete(false)}>Cancel</button>
            </>
          ) : isAmendment ? (
            <>
              <button style={{ ...S.btnDanger, marginRight: 'auto' }} onClick={handleDelete} disabled={isBusy}>Delete Draft</button>
              <button style={S.btnTertiary} onClick={onClose} disabled={isBusy}>Cancel</button>
              <button
                style={{ ...S.btnPrimary, opacity: canSendDraft ? 1 : 0.5, cursor: canSendDraft ? 'pointer' : 'not-allowed' }}
                onClick={() => { if (canSendDraft) setShowSendConfirm(true); }} disabled={isBusy || !canSendDraft}
                title={sendBlockerReason || undefined}
              >
                Send Amended PO
              </button>
            </>
          ) : (
            <>
              <button style={{ ...S.btnDanger, marginRight: 'auto' }} onClick={handleDelete} disabled={isBusy}>Delete Draft</button>
              <button style={S.btnTertiary} onClick={onClose} disabled={isBusy}>Cancel</button>
              <button style={S.btnSecondary} onClick={handleSave} disabled={isBusy}>
                {saving ? 'Saving…' : 'Save Draft'}
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <button
                  style={{ ...S.btnPrimary, opacity: canSendDraft ? 1 : 0.5, cursor: canSendDraft ? 'pointer' : 'not-allowed' }}
                  onClick={() => { if (canSendDraft) setShowSendConfirm(true); }}
                  disabled={isBusy || !canSendDraft}
                  title={sendBlockerReason || undefined}
                >
                  Approve & Send PO
                </button>
                {sendBlockerReason && !canSendDraft && (
                  <div style={{ fontSize: 11, color: '#b71c1c', textAlign: 'right' }}>{sendBlockerReason}</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {showVendorContactModal && (
        <VendorContactModal
          vendorId={po.vendor_id || null}
          vendorName={po.vendor_name_snapshot || null}
          storeId={po.store_id || storeIdProp || null}
          focusField={vendorModalFocusField}
          onClose={() => { autoOpenedForThisModalRef.current = false; setShowVendorContactModal(false); setVendorModalFocusField(null); }}
          onSaved={handleVendorContactSaved}
        />
      )}
      {orderingRulesOption && (
        <OrderingRulesEditor
          option={orderingRulesOption}
          onClose={() => setOrderingRulesOption(null)}
          onSaved={(updated) => {
            // Merge the fresh rules so validation re-runs against them
            // immediately — the buyer's typed quantities are untouched (a
            // constraint change never silently rewrites an order, it only
            // changes what is legal).
            if (updated?.id != null) {
              setLinkedOptionsById((prev) => ({ ...prev, [String(updated.id)]: { ...prev[String(updated.id)], ...updated } }));
            }
          }}
        />
      )}
      {showSendConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(43,29,20,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 16, paddingRight: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '0 8px 40px rgba(43,29,20,0.22)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 22px 16px', borderBottom: '1px solid #e8e0d5' }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#2b1d14', marginBottom: 4 }}>
                {isAmendment ? 'Send Amended PO?' : 'Approve & Send PO?'}
              </div>
              <div style={{ fontSize: 13, color: '#7b6b61' }}>Review the details below before sending.</div>
            </div>
            <div style={{ padding: '16px 22px' }}>
              <div style={{ fontSize: 13, color: '#2b1d14', lineHeight: 1.7 }}>
                <div><strong>Vendor:</strong> {po.vendor_name_snapshot || '—'}</div>
                <div><strong>To:</strong> {recipients.join(', ')}</div>
                <div><strong>Items:</strong> {totalCount} line{totalCount !== 1 ? 's' : ''}</div>
                <div><strong>Total:</strong> {hasEstimatedLines ? `~${formatPoCurrency(total)}` : formatPoCurrency(total)}</div>
                <div><strong>Delivery:</strong> {deliveryDate ? describeDeliveryDate(deliveryDate) : 'No date requested'}</div>
                {(deliveryInstructions || '').trim() && (
                  <div><strong>Instructions:</strong> {deliveryInstructions}</div>
                )}
                {(deliveryNotes || '').trim() && (
                  <div><strong>Notes for this order:</strong> {deliveryNotes}</div>
                )}
              </div>
              <div style={{ marginTop: 12, fontSize: 13, color: '#7b6b61' }}>
                {isAmendment
                  ? 'This will email the vendor an amended order reflecting the quantities above.'
                  : 'This will email the vendor a purchase order for the items listed above.'}
              </div>
            </div>
            <div style={{ padding: '12px 22px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                style={S.btnTertiary}
                onClick={() => setShowSendConfirm(false)}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                style={{ ...S.btnPrimary, opacity: sending ? 0.7 : 1 }}
                onClick={() => handleSend()}
                disabled={sending}
              >
                {sending ? 'Sending…' : isAmendment ? 'Yes, Send Amended PO' : 'Yes, Send PO'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
